// @flow

import React from 'react';
import _ from 'lodash';
import { Tracking } from 'web-core/src/tracking';

import AccountForm from 'client/components/account/accountForm';
import FormErrorNotification from 'client/components/form/formErrorNotification';
import Hero from 'client/components/account/hero';
import LinkReplace from 'client/components/button/linkReplace';
import Translate from 'client/components/translate';
import PROVISION_ENABLE from 'client/constants/enterpriseProvision';
import { handle422Error, handleRedirect } from 'routes/join/utils/index';
import { WEB_CLICK_PROVISION } from 'routes/join/constants/tracking';
import type { LocationType, RouterType } from 'types/react-router.flow';

const PARTNER_PREFIX = 'partner.enterprise.account.create';
const COMMON_PREFIX = 'common.account.create';

type Props = {
  brand: string,
  code: string | null,
  createAndProvision: Function,
  createWithResources: Function,
  id: string | null,
  isProvisioned: boolean,
  location: LocationType,
  redirect: string,
  router: RouterType,
  siteConfigs: Object,
  translations: Object,
};

type State = {
  error: string | null,
  isSubmitting: boolean,
};

type CreateHandlerType = {
  email: string,
  password: string,
};

class CreatePage extends React.PureComponent<Props, State> {
  static defaultProps = {
    code: null,
    redirect: '',
    id: null,
  };

  state = {
    error: null,
    isSubmitting: false,
  };

  bindCreateHandler = ({ email, password }: CreateHandlerType) => {
    const { brand, code, id, isProvisioned, redirect, router, translations } = this.props;
    this.setSubmitting(true);

    // Using createAndProvision when redirect route or provision for that route is enabled
    const createUser =
      !redirect || PROVISION_ENABLE[redirect]
        ? this.props.createAndProvision
        : this.props.createWithResources;

    const source = brand === 'stride' ? brand : `enterprise-${brand}`;

    return createUser({
      apps: [],
      code,
      email,
      members: [],
      password,
      source,
      id,
    })
      .then((data) => {
        const user_id = _.get(data, 'user.oid', _.get(data, 'data.user.oid'));

        handleRedirect(redirect, user_id, data, router);
      })
      .catch((err) => {
        const serverError = _.get(err, 'response.status');
        if (serverError === 422) {
          handle422Error(isProvisioned, err, router, redirect);
        } else {
          const existingAccount = _.get(err, 'response.data.message', '').indexOf('exists') > -1;
          this.setState({
            error: _.get(
              translations,
              `common.error.${existingAccount ? 'existingAccount' : 'generic'}`,
            ),
            isSubmitting: false,
          });
        }
      })
      .finally(() => {
        // enable tracking if no redirect defined or provision for route is enabled
        if (!redirect || PROVISION_ENABLE[redirect]) {
          Tracking.track(WEB_CLICK_PROVISION, { type: 'create', client: brand });
        }
      });
  };

  setSubmitting = (isSubmitting: boolean) => {
    this.setState({ isSubmitting });
  };

  render() {
    const { brand, location, siteConfigs } = this.props;
    const { error, isSubmitting } = this.state;
    return (
      <div className="account-page text-center">
        <Hero brandName={brand} siteConfigs={siteConfigs} />
        {this.state.error && (
          <FormErrorNotification
            translateKey="partner.enterprise.error"
            translateUsing={{ errorMessage: error }} />
        )}
        <div className="center-col-small">
          <div className="margin-2">
            <h4 className="semi-bold">
              <Translate path={`${PARTNER_PREFIX}.header`} />
            </h4>
          </div>
          <AccountForm
            formType="create"
            isSubmitting={isSubmitting}
            submitHandler={this.bindCreateHandler} />
          <p className="mini margin">
            <Translate path={`${COMMON_PREFIX}.legal`} />
          </p>
          <p className="pad-bottom">
            <Translate path={`${COMMON_PREFIX}.changeCta`} />
            <LinkReplace
              to={{
                pathname: '/login',
                query: location.query,
              }}>
              <Translate path={`${COMMON_PREFIX}.changeCtaLink`} />
            </LinkReplace>
          </p>
        </div>
      </div>
    );
  }
}

export default CreatePage;
